import React, { useState, useEffect, useCallback } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service } from '@/services/pmt2Service';
import { Timestamp } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'; // Import table components
import { Skeleton } from '@/components/ui/skeleton';

interface PriceEntry {
  id: string;
  date: Timestamp; // Primary date field
  Date?: string; // Optional string date field (less reliable)
  'Your Name': string;
  'New Value': string; // Price value
  note?: string; // Optional note field
  [key: string]: any;
}

interface PriceHistoryDashboardProps {
  product: PMT2Product | null;
}

/**
 * Price History Dashboard Component
 * Displays the history of price changes for a selected product in a table format.
 */
const PriceHistoryDashboard: React.FC<PriceHistoryDashboardProps> = ({ product }) => {
  const [priceHistory, setPriceHistory] = useState<PriceEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPriceHistory = useCallback(async (companyName: string, asin: string) => {
    setPriceHistory([]);
    setError(null);
    setLoading(true);
    try {
      const entries = await pmt2Service.getPriceHistory(companyName, asin);
      const validatedEntries = entries.map(e => ({
          ...e,
          date: e.date || Timestamp.fromDate(new Date(0))
      }));
      if (product && product.id === asin && product.companyName === companyName) {
          setPriceHistory(validatedEntries);
      }
    } catch (err) {
      console.error('Error fetching price history:', err);
      setError('Failed to load price history.');
    } finally {
       setLoading(false);
    }
  }, [product]);

  useEffect(() => {
    if (product) {
      fetchPriceHistory(product.companyName, product.id);
    } else {
      setPriceHistory([]);
      setError(null);
      setLoading(false);
    }
  }, [product, fetchPriceHistory]);

  // --- Helper Functions ---
   const parseSafeFloat = (value: any): number | null => {
      if (value === null || value === undefined || value === '') return null;
      const cleanedValue = String(value).replace(/[$,%]/g, '').replace(/,/g, '');
      const num = parseFloat(cleanedValue);
      return isNaN(num) ? null : num;
  };

  const calculateDaysAgo = (timestamp: Timestamp | null): number => {
    if (!timestamp?.toDate) return 0;
    try {
      const entryDate = timestamp.toDate();
      // Create dates based purely on UTC year, month, day to avoid timezone/time issues
      const entryDateUTC = Date.UTC(entryDate.getUTCFullYear(), entryDate.getUTCMonth(), entryDate.getUTCDate());
      const today = new Date();
      const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());

      const diffTime = todayUTC - entryDateUTC; // Difference in milliseconds
      // Add 1 day to match Sheets calculation convention
      return Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1);
    } catch { return 0; }
  };


  const formatDate = (timestamp: Timestamp | null): string => {
    if (!timestamp?.toDate) return 'Unknown';
    try {
      // Format as MM/DD/YY using UTC date parts
       const date = timestamp.toDate();
       const month = (date.getUTCMonth() + 1).toString();
       const day = date.getUTCDate().toString();
       const year = date.getUTCFullYear().toString().slice(-2);
       return `${month}/${day}/${year}`;
    } catch { return 'Invalid'; }
  };

  const formatPrice = (priceStr: string | number | null | undefined): string => {
    if (priceStr === null || priceStr === undefined || priceStr === '') return '-';
    try {
      const cleanedPrice = String(priceStr).replace(/[^\d.-]/g, '');
      const price = parseFloat(cleanedPrice);
      if (isNaN(price)) return '-';
      return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } catch {
      return '-';
    }
  };
  // --- End Helpers ---

  // Sort entries by date (newest first) using the reliable timestamp
  const sortedEntries = [...priceHistory].sort((a, b) => {
    const timeA = a.date?.toDate?.()?.getTime() || 0;
    const timeB = b.date?.toDate?.()?.getTime() || 0;
    return timeB - timeA;
  });

  const headers = ['Days Ago', 'Date', 'Your Name', 'New Value', 'Note'];

  // --- Render Logic ---
  const renderTableContent = () => {
    if (loading) {
      return (
        // Skeleton Row
        <TableRow>
          {headers.map((_, index) => (
            <TableCell key={`skel-${index}`} className="py-2 px-3 text-sm"> {/* Font size */}
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={headers.length} className="text-center text-red-600 py-4 text-sm"> {/* Font size */}
            {error}
          </TableCell>
        </TableRow>
      );
    }

    if (sortedEntries.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={headers.length} className="text-center text-gray-500 py-4 text-sm"> {/* Font size */}
            No price history found for this product.
          </TableCell>
        </TableRow>
      );
    }

    return sortedEntries.map((entry, index) => (
      <TableRow key={`entry_${index}_${entry.id || Math.random()}`}>
        <TableCell className="py-2 px-3 text-sm whitespace-nowrap">{calculateDaysAgo(entry.date)}</TableCell>
        <TableCell className="py-2 px-3 text-sm whitespace-nowrap">{formatDate(entry.date)}</TableCell>
        <TableCell className="py-2 px-3 text-sm whitespace-nowrap">{entry['Your Name'] || '-'}</TableCell>
        <TableCell className="py-2 px-3 text-sm whitespace-nowrap">{formatPrice(entry['New Value'])}</TableCell>
        <TableCell className="py-2 px-3 text-sm whitespace-nowrap">{entry.note || '-'}</TableCell>
      </TableRow>
    ));
  };


  return (
    // Added wrapper div for max-width
    <div className="max-w-3xl mx-auto">
        <Card>
        <CardHeader className="p-4">
            <CardTitle className="text-2xl font-bold">Price History</CardTitle>
        </CardHeader>
        <CardContent className="p-0"> {/* Remove padding for table */}
            <Table>
            <TableHeader>
                <TableRow>
                {headers.map(header => (
                    <TableHead key={header} className="py-2 px-3 text-xs">{header}</TableHead>
                ))} {/* Corrected parenthesis */}
                </TableRow>
            </TableHeader>
            <TableBody>
                {renderTableContent()}
            </TableBody>
            </Table>
        </CardContent>
        </Card>
    </div>
  );
};

export default PriceHistoryDashboard;